import { calculateIncomeTax, type IncomeTaxRuleSet } from "../income-tax/index.js";
import {
  type HelpWarningInput,
  HelpWarningInputSchema,
  type HelpWarningOutput,
  type PayRiseInput,
  PayRiseInputSchema,
  type PayRiseOutput,
  type SecondJobInput,
  SecondJobInputSchema,
  type SecondJobOutput,
  type TakeHomeInput,
  TakeHomeInputSchema,
  type TakeHomeOutput,
  type TakeHomeParsed,
  type WithholdingGapInput,
  WithholdingGapInputSchema,
  type WithholdingGapOutput,
} from "./types.js";
import type { TakeHomeRuleSet } from "./fy2025-26.js";

// ---------------------------------------------------------------------------
// The net-pay PRODUCT surfaces. Each is a thin pure function over the shared
// `income-tax` engine — none re-implements tax arithmetic. A pay rise, a HELP
// warning, a withholding reconciliation and a second-job check are all just
// different views over `calculateIncomeTax`. Pure: same inputs + rulesets
// always yield the same output; no I/O, no Date.now() (the FY is a parameter).
// ---------------------------------------------------------------------------

/**
 * Hard-rule guard (panel lens 2/3 made binding): income tax is not a s764A
 * financial product and there is NO compliant paid handoff from a personalised
 * net-pay result. The dangerous adjacencies the panels flag — "personal loan /
 * BNPL to cover your tax bill", "refinance your HELP", "salary sacrifice into
 * super" — are the C2 AFSL kill-line and must never reach an output. This guard
 * runs over every result's serialised form and throws if a financial-product /
 * credit identifier leaks in. Tested in calculate.test.ts.
 */
const FORBIDDEN_HANDOFF_TERMS = [
  "personal loan",
  "refinance",
  "salary sacrifice",
  "salary-sacrifice",
  "bnpl",
  "buy now pay later",
  "credit card",
  "invest instead",
  "should switch",
  "you should",
  "save $",
];

export function assertNoHandoff(output: unknown): void {
  const serialised = JSON.stringify(output).toLowerCase();
  for (const term of FORBIDDEN_HANDOFF_TERMS) {
    if (serialised.includes(term)) {
      throw new Error(
        `take-home output leaked a forbidden financial-product/advice term: "${term}"`,
      );
    }
  }
}

/** Round to whole dollars (the engine already rounds; we round derived sums). */
function round(n: number): number {
  return Math.round(n);
}

/** Run the shared income-tax engine for a take-home-shaped input + ruleset. */
function runEngine(
  input: TakeHomeParsed,
  taxRules: IncomeTaxRuleSet,
): ReturnType<typeof calculateIncomeTax> {
  return calculateIncomeTax(
    {
      taxableIncome: input.grossIncome,
      residency: input.residency,
      hasStudyLoan: input.hasStudyLoan,
      household: {
        family: input.family,
        dependants: input.dependants,
        seniorPensioner: false,
      },
      assessmentYear: input.assessmentYear,
    },
    taxRules,
  );
}

/**
 * Net pay from gross — the anchor surface. Delegates wholesale to the income-tax
 * engine and reshapes its output into a net-pay breakdown.
 */
export function takeHome(
  rawInput: TakeHomeInput,
  rules: TakeHomeRuleSet,
  taxRules: IncomeTaxRuleSet,
): TakeHomeOutput {
  const input = TakeHomeInputSchema.parse(rawInput);
  void rules; // FY provenance is carried by the ruleset; figures come from taxRules.
  const a = runEngine(input, taxRules);

  const withheld = a.netTax + a.helpRepayment;
  const averageRate = input.grossIncome > 0 ? withheld / input.grossIncome : 0;

  const out: TakeHomeOutput = {
    grossIncome: input.grossIncome,
    incomeTax: a.incomeTax,
    medicareLevy: a.medicareLevy,
    lito: a.lito,
    netTax: a.netTax,
    helpRepayment: a.helpRepayment,
    takeHome: a.takeHome,
    averageRate,
  };
  assertNoHandoff(out);
  return out;
}

/**
 * What a pay rise ACTUALLY adds. The keep-rate is computed by differencing two
 * full assessments — at the current income and at income + rise — so the
 * marginal income tax, Medicare levy, LITO claw-back AND the marginal HELP/STSL
 * step (15c/$ over $67k, etc.) all fall out exactly, with no re-derivation of
 * rates. This is the headline mode: it shows the true keep-rate, not the
 * bracket. Information only — no "ask for more", no "you should".
 *
 * Worked example (pay-rise ledger #3, CITED): $10k rise on $90k, HELP, no MLS →
 * Δtax $3,000 + ΔMedicare $200 + ΔHELP $1,500 = $4,700 withheld → keep $5,300
 * (53% keep-rate).
 */
export function payRiseMarginal(
  rawInput: PayRiseInput,
  rules: TakeHomeRuleSet,
  taxRules: IncomeTaxRuleSet,
): PayRiseOutput {
  const input = PayRiseInputSchema.parse(rawInput);
  void rules;

  const before = runEngine(input, taxRules);
  const after = runEngine(
    { ...input, grossIncome: input.grossIncome + input.riseAmount },
    taxRules,
  );

  const extraIncomeTax = round(after.incomeTax - before.incomeTax);
  const extraMedicareLevy = round(after.medicareLevy - before.medicareLevy);
  // LITO falls as income rises (the taper). A drop in offset is extra net tax;
  // surface it as a positive claw-back cost on the rise.
  const litoClawback = round(before.lito - after.lito);
  const extraHelpRepayment = round(after.helpRepayment - before.helpRepayment);

  // Total kept is computed from net tax + HELP so it always reconciles to the
  // breakdown (incomeTax + Medicare − LITO drop = Δ net tax).
  const totalDeductions = round(
    after.netTax + after.helpRepayment - (before.netTax + before.helpRepayment),
  );
  const keptAmount = round(input.riseAmount - totalDeductions);
  const keepRate = input.riseAmount > 0 ? keptAmount / input.riseAmount : 0;

  const out: PayRiseOutput = {
    grossIncome: input.grossIncome,
    riseAmount: input.riseAmount,
    extraIncomeTax,
    extraMedicareLevy,
    litoClawback,
    extraHelpRepayment,
    totalDeductions,
    keptAmount,
    keepRate,
    effectiveMarginalRate: input.riseAmount > 0 ? 1 - keepRate : 0,
  };
  assertNoHandoff(out);
  return out;
}

/**
 * Silent-bill warning for an undeclared HELP/STSL debt. The compulsory
 * repayment is assessed in full at lodgement regardless of withholding; STSL
 * withholding is opt-in, so an employee who never told their payer has nothing
 * withheld and the WHOLE repayment lands as a lump-sum bill.
 *
 * Worked example (help-not-withheld ledger #5, CITED): $90,000 salary, HELP not
 * withheld → (90,000 − 67,000) × 0.15 = $3,450 lump-sum bill at lodgement.
 */
export function helpNotWithheldWarning(
  rawInput: HelpWarningInput,
  rules: TakeHomeRuleSet,
  taxRules: IncomeTaxRuleSet,
): HelpWarningOutput {
  const input = HelpWarningInputSchema.parse(rawInput);
  void rules;
  const a = runEngine({ ...input, hasStudyLoan: true }, taxRules);

  const compulsoryRepayment = a.helpRepayment;
  // If the employer withholds STSL, the loan is covered through the year and
  // there is no loan-attributable lump sum. Otherwise the whole repayment bills.
  const predictedLumpSumBill = input.employerWithholdsStsl
    ? 0
    : compulsoryRepayment;

  const out: HelpWarningOutput = {
    repaymentIncome: a.helpRepaymentIncome,
    compulsoryRepayment,
    employerWithholdsStsl: input.employerWithholdsStsl,
    predictedLumpSumBill,
    silentBill: !input.employerWithholdsStsl && compulsoryRepayment > 0,
  };
  assertNoHandoff(out);
  return out;
}

/**
 * Withheld-vs-annual-liability reconciliation → predicted refund/bill. The
 * annual liability comes from the engine (net tax + compulsory HELP); the user
 * supplies what was actually withheld. The signed gap predicts the refund
 * (over-withheld) or bill (under-withheld). Information only — the ATO
 * finalises the actual assessment.
 */
export function withholdingGap(
  rawInput: WithholdingGapInput,
  rules: TakeHomeRuleSet,
  taxRules: IncomeTaxRuleSet,
): WithholdingGapOutput {
  const input = WithholdingGapInputSchema.parse(rawInput);
  void rules;
  const a = runEngine(input, taxRules);

  const annualNetTax = a.netTax;
  const annualHelpRepayment = a.helpRepayment;
  const annualLiability = annualNetTax + annualHelpRepayment;
  const totalWithheld = round(input.taxWithheld + input.stslWithheld);
  const gap = round(totalWithheld - annualLiability);

  const out: WithholdingGapOutput = {
    annualNetTax,
    annualHelpRepayment,
    annualLiability,
    totalWithheld,
    gap,
    predictedRefund: gap > 0 ? gap : 0,
    predictedBill: gap < 0 ? -gap : 0,
    outcome: gap > 0 ? "refund" : gap < 0 ? "bill" : "balanced",
  };
  assertNoHandoff(out);
  return out;
}

/**
 * Second-job under-withholding from claiming the tax-free threshold on the
 * wrong payer. When the threshold is claimed on the second job too, the second
 * payer treats that job's first $18,200 as tax-free — but the threshold is
 * already consumed by job 1, so that slice should have been taxed at the user's
 * true marginal rate. Under-withholding ≈ doubleCounted × marginalRate.
 *
 * Worked example (second-job ledger #5, AGENT_VERIFIED): combined income puts
 * the user in the 30% band; ~$18,200 of second-job income is wrongly treated as
 * tax-free → ≈ $18,200 × 0.30 ≈ $5,460 shortfall (four-figure, as the ledger
 * states; at the 16% band ≈ $2,912).
 */
export function secondJobWithholding(
  rawInput: SecondJobInput,
  rules: TakeHomeRuleSet,
  taxRules: IncomeTaxRuleSet,
): SecondJobOutput {
  const input = SecondJobInputSchema.parse(rawInput);
  const combinedIncome = input.primaryJobIncome + input.secondJobIncome;

  // The user's TRUE marginal rate at combined income, from the shared engine —
  // the rate that slice of second-job income should have been taxed at.
  const combined = calculateIncomeTax(
    {
      taxableIncome: combinedIncome,
      residency: input.residency,
      assessmentYear: input.assessmentYear,
    },
    taxRules,
  );
  const marginalRate = combined.marginalRate;

  // The wrongly-tax-free slice is the threshold, capped at the second job's
  // income (you can't double-count more than the second job pays).
  const doubleCountedThreshold = input.claimsThresholdOnSecondJob
    ? Math.min(rules.taxFreeThreshold, input.secondJobIncome)
    : 0;
  const predictedShortfall = round(doubleCountedThreshold * marginalRate);

  const out: SecondJobOutput = {
    combinedIncome,
    claimsThresholdOnSecondJob: input.claimsThresholdOnSecondJob,
    doubleCountedThreshold,
    marginalRate,
    predictedShortfall,
  };
  assertNoHandoff(out);
  return out;
}
